/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';
import { useStoreLocatorForm } from '@/extensions/store-locator/hooks/use-store-locator-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

/**
 * StoreLocatorForm
 *
 * ZIP-code search for the store locator panel.
 *
 * SHAPE IS TAKEN FROM LIVE NM, not from the previous template form. Owner capture
 * `.reference/owner-store-selector-2026-08-23/Screenshot 2026-08-23 at 2.53.53 PM.png`
 * (address bar neimanmarcus.com, DevTools Responsive 400x909, so the width is stated
 * in frame) shows ONE rule-underlined row: the ZIP on the left, `SEARCH` right-aligned
 * on the SAME row. There is no country selector, no "Or" divider, and no
 * "Use My Location" control anywhere in the panel.
 *
 * Three deliberate divergences from that capture, each with a reason:
 *
 *  - Live's field reads "Showing near 80202" because live geolocates a brand-new guest
 *    silently on first load. We do NOT do that -- it is a privacy-sensitive default and
 *    it degrades to nothing in a headless session. The prefix renders only once a search
 *    has actually been run, so the copy never claims a location we do not have.
 *  - The country code is a constant (`US`) in `use-store-locator-form`, because SCAPI's
 *    store-search 400s without either a countryCode or coordinates.
 *  - `SEARCH` is a real submit button rather than a link, so the row is keyboard-operable.
 *
 * @returns ReactElement
 */
export default function StoreLocatorForm(): ReactElement {
    const { t } = useTranslation('extStoreLocator');
    const { form, onSubmit } = useStoreLocatorForm();
    // `searchParams` is non-null only after a search has actually been submitted, so the
    // "Showing near" prefix can never assert a location we have not looked up.
    const hasSearched = Boolean(useStoreLocator((s) => s.searchParams)?.postalCode);

    /*
        HYDRATION ADOPTION -- the ZIP is server-rendered, so a shopper can type
        into it before React owns the node, and everything typed was then thrown
        away. MEASURED on the running app at 1280, three runs, typing `80202`
        starting at `domcontentloaded`: the field ended holding `""` every time.
        A value-sampling trace showed the DOM node holds the full `80202` from
        t=361ms and is cleared in ONE step at t=476ms -- not character by
        character, which is the tell that this is React writing the controlled
        `value=""` over it rather than the keystrokes failing to land.

        The cause is NOT the hydration mutation itself and it is NOT the
        `form.reset` effect in `use-store-locator-form` -- a previous pass
        disabled that effect outright and measured no change. It is the first
        post-hydration render of a CONTROLLED input: React Hook Form's state
        still holds the `''` it was constructed with, so the next commit pushes
        `''` into a node the shopper had already typed into.

        So the node still carries the typed characters at the moment the tree
        mounts, and adopting them into form state before that commit is enough.
        The capture happens in the ref callback rather than in the effect below
        because ref callbacks run in the commit's layout phase, ahead of EVERY
        passive effect -- including `use-store-locator-form`'s reset, which is
        registered first and would otherwise be ordered ahead of us. Splitting
        capture from apply is what makes this independent of that ordering
        instead of incidentally surviving it.

        It degrades correctly. When nothing was typed the captured string is
        empty and the effect is a no-op, which is the case on every mount that
        is not a fresh SSR page -- the panel opened from the PDP's `Find In
        Store` button mounts after hydration, so it never has anything to adopt.
    */
    const preHydrationPostalCode = useRef<string | null>(null);
    const captureSearchRow = useCallback((node: HTMLDivElement | null) => {
        if (node && preHydrationPostalCode.current === null) {
            preHydrationPostalCode.current = node.querySelector('input')?.value ?? '';
        }
    }, []);

    useEffect(() => {
        const typed = preHydrationPostalCode.current;
        if (typed && typed !== form.getValues('postalCode')) {
            form.setValue('postalCode', typed, { shouldDirty: true });
        }
    }, [form]);

    return (
        <Form {...form}>
            <form
                onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
                aria-label={t('storeLocator.form.searchFormAriaLabel')}>
                <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                        <FormItem className="gap-0">
                            <FormLabel className="sr-only">{t('storeLocator.form.postalCodeLabel')}</FormLabel>
                            <div
                                ref={captureSearchRow}
                                className="flex items-center gap-3 border-b border-foreground pb-2"
                                data-testid="store-locator-search-row">
                                {hasSearched && (
                                    <span className="shrink-0 text-base text-foreground">
                                        {t('storeLocator.form.showingNear')}
                                    </span>
                                )}
                                <FormControl>
                                    <input
                                        {...field}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="postal-code"
                                        aria-label={t('storeLocator.form.postalCodeLabel')}
                                        placeholder={t('storeLocator.form.postalCodePlaceholder')}
                                        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-hidden placeholder:text-muted-foreground focus-visible:ring-0"
                                    />
                                </FormControl>
                                <button
                                    type="submit"
                                    data-testid="store-locator-search-button"
                                    className="shrink-0 text-sm font-semibold tracking-wide uppercase hover:opacity-60 transition-opacity">
                                    {t('storeLocator.form.searchButton')}
                                </button>
                            </div>
                            <FormMessage className="pt-2" />
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    );
}
